
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, deleteField, serverTimestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { CartItem, CartData } from '../types';
import { notify } from '../lib/toast';

interface CartContextType {
    items: Record<string, CartItem>;
    totalItems: number;
    loading: boolean;
    isSyncing: boolean;
    addToCart: (item: Omit<CartItem, 'addedAt'>) => Promise<void>;
    removeFromCart: (listingId: string) => Promise<void>;
    clearCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

const LOCAL_STORAGE_KEY = 'quickswap_cart_guest';

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [items, setItems] = useState<Record<string, CartItem>>({});
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);

    // Calculated total
    const totalItems = Object.values(items).reduce((acc, item) => acc + item.quantity, 0);

    // 1. Initial Load (Guest or User)
    useEffect(() => {
        let unsubscribe: () => void;

        const initCart = async () => {
            setLoading(true);

            if (currentUser) {
                // === CLOUD MODE ===
                // 1. Check for Merge (Guest -> Cloud)
                const localCartJson = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (localCartJson) {
                    setIsSyncing(true);
                    try {
                        const localItems: Record<string, CartItem> = JSON.parse(localCartJson);
                        // Merge to Cloud
                        const cartRef = doc(db, 'users', currentUser.id, 'cart', 'active');
                        // We use a Map to merge: prioritizing Cloud if conflict (or strictly Union?)
                        // Simple Strategy: Overwrite Cloud with Local if Local is newer?
                        // Better: Just add Local items to Cloud if they don't exist.

                        // For simplicity in Phase 15A: We'll just write each local item to cloud
                        const updateData: any = {};
                        Object.values(localItems).forEach(item => {
                            updateData[`items.${item.listingId}`] = item;
                        });
                        updateData['updatedAt'] = serverTimestamp();

                        // We use setDoc with merge to ensure doc exists
                        await setDoc(cartRef, updateData, { merge: true });

                        // Clear Local
                        localStorage.removeItem(LOCAL_STORAGE_KEY);
                        notify.success('Sepetiniz hesabınızla eşleştirildi.');
                    } catch (error) {
                        console.error("Cart Merge Error:", error);
                    } finally {
                        setIsSyncing(false);
                    }
                }

                // 2. Subscribe to Cloud
                unsubscribe = onSnapshot(doc(db, 'users', currentUser.id, 'cart', 'active'), (docSnap) => {
                    if (docSnap.exists()) {
                        const data = docSnap.data() as CartData;
                        setItems(data.items || {});
                    } else {
                        setItems({});
                    }
                    setLoading(false);
                }, (err) => {
                    console.error("Cart subscription error:", err);
                    setLoading(false);
                });

            } else {
                // === GUEST MODE ===
                const localCartJson = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (localCartJson) {
                    try {
                        setItems(JSON.parse(localCartJson));
                    } catch (e) {
                        console.error("Failed to parse local cart", e);
                        setItems({});
                    }
                } else {
                    setItems({});
                }
                setLoading(false);
            }
        };

        initCart();

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [currentUser]);

    // Actions
    const addToCart = async (itemInput: Omit<CartItem, 'addedAt'>) => {
        const item: CartItem = {
            ...itemInput,
            addedAt: Date.now(),
        };

        // Optimistic Update
        const newItems = { ...items, [item.listingId]: item };
        setItems(newItems);

        if (currentUser) {
            // Cloud Write
            try {
                const cartRef = doc(db, 'users', currentUser.id, 'cart', 'active');
                await setDoc(cartRef, {
                    items: {
                        [item.listingId]: item
                    },
                    updatedAt: serverTimestamp()
                }, { merge: true });

                notify.success('Sepete eklendi');
            } catch (err) {
                console.error("Add to cart failed", err);
                notify.error('Sepete eklenirken hata oluştu');
                // Revert
                const reverted = { ...items };
                delete reverted[item.listingId];
                setItems(reverted);
            }
        } else {
            // Local Write
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newItems));
            notify.success('Sepete eklendi');
        }
    };

    const removeFromCart = async (listingId: string) => {
        const newItems = { ...items };
        delete newItems[listingId];
        setItems(newItems); // Optimistic

        if (currentUser) {
            try {
                const cartRef = doc(db, 'users', currentUser.id, 'cart', 'active');
                await updateDoc(cartRef, {
                    [`items.${listingId}`]: deleteField(),
                    updatedAt: serverTimestamp()
                });
            } catch (err) {
                console.error("Remove from cart failed", err);
                // Revert logic would handle fetch again or complex undo
            }
        } else {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newItems));
        }
    };

    const clearCart = async () => {
        setItems({}); // Optimistic

        if (currentUser) {
            try {
                // Delete the whole doc or just items? 
                // Better to just clear items to keep doc metadata maybe?
                // Or delete doc is cleaner.
                const cartRef = doc(db, 'users', currentUser.id, 'cart', 'active');
                await setDoc(cartRef, { items: {}, updatedAt: serverTimestamp() });
            } catch (err) {
                console.error("Clear cart failed", err);
            }
        } else {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
    };

    return (
        <CartContext.Provider value={{
            items,
            totalItems,
            loading,
            isSyncing,
            addToCart,
            removeFromCart,
            clearCart
        }}>
            {children}
        </CartContext.Provider>
    );
};
